import appVersion from '../../../appVersion.json';

export const PaymentErrorCode = {
  NETWORK_ERROR: 'NETWORK_ERROR',
  GATEWAY_DECLINED: 'GATEWAY_DECLINED',
  INVALID_ORDER: 'INVALID_ORDER',
  ALREADY_PAID: 'ALREADY_PAID',
  TIMEOUT: 'TIMEOUT',
  USER_CANCELLED: 'USER_CANCELLED',
  CONFIG_ERROR: 'CONFIG_ERROR',
  UNKNOWN: 'UNKNOWN',
} as const;

export type PaymentErrorCode =
  (typeof PaymentErrorCode)[keyof typeof PaymentErrorCode];

export class PaymentError extends Error {
  code: PaymentErrorCode;
  gatewayId?: string;
  country?: string;
  orderId?: string;

  constructor(
    code: PaymentErrorCode,
    message: string,
    context?: { gatewayId?: string; country?: string; orderId?: string }
  ) {
    super(message);
    this.name = 'PaymentError';
    this.code = code;
    this.gatewayId = context?.gatewayId;
    this.country = context?.country;
    this.orderId = context?.orderId;
  }
}

/**
 * Single reporting path replacing the `console.error` scattered across the
 * old India/Thailand components. No Sentry project is wired up yet — that
 * needs a DSN from the team — but every call site already tags gateway/
 * country/order plus the exact build (`appVersion.json`, stamped with the
 * tenant/stage it was built for by every start/build script — see
 * scripts/generate-version.js), so a bug report or Sentry event can always
 * be tied back to precisely which build, for which country, produced it.
 * Plugging Sentry in later is a one-line change here, not a sweep through
 * every catch block again.
 *
 * `buildCountry`/`buildStage` (from the build) are kept separate from
 * whatever `country` a call site passes in `context` (the runtime-resolved
 * country for that specific error) rather than merged into one `country`
 * key — if they ever disagree, that mismatch is itself a signal something
 * is misconfigured (e.g. the wrong tenant's build deployed to the wrong
 * domain), which a single overwritten field would hide.
 */
export function reportError(
  error: unknown,
  context?: Record<string, unknown>
): void {
  const taggedContext = {
    ...context,
    appVersion: appVersion.version,
    buildCountry: appVersion.country,
    buildStage: appVersion.stage,
    buildId: appVersion.buildId,
  };

  // eslint-disable-next-line no-console
  console.error('[payment]', error, taggedContext);

  const sentry = (
    window as unknown as {
      Sentry?: {
        captureException: (
          error: unknown,
          options?: { extra?: Record<string, unknown> }
        ) => void;
      };
    }
  ).Sentry;

  if (sentry) {
    sentry.captureException(error, { extra: taggedContext });
  }
}
